import { useState } from 'react';

import { Text } from '@spotify-internal/encore-web';
import { DialogComposite } from '@spotify/confidence-core-react';
import { ObservationsV1IssueAutonomyLevel } from '@spotify/confidence-graphql';
import { getAutonomyLevelLabel } from '@spotify/plugin-confidence-issues-react';

import type { ReviewDecision } from './reviewFeedback';

import styles from './FinishReviewDialog.module.css';

type ReviewDecisionOption = {
  autonomyLevel: ObservationsV1IssueAutonomyLevel;
  description: string;
  label: string;
  value: ReviewDecision;
};

const REVIEW_DECISION_OPTIONS: ReviewDecisionOption[] = [
  {
    value: 'request_changes',
    label: 'Request changes',
    description:
      'Ask Confidence to revise the plan and wait for another review.',
    autonomyLevel: ObservationsV1IssueAutonomyLevel.Investigate,
  },
  {
    value: 'approve_build',
    label: 'Approve and build',
    description:
      'Start implementing the plan and check in before major decisions.',
    autonomyLevel: ObservationsV1IssueAutonomyLevel.Build,
  },
  {
    value: 'approve_full',
    label: 'Approve and run autonomously',
    description:
      'Let Confidence take the issue from the approved plan to completion.',
    autonomyLevel: ObservationsV1IssueAutonomyLevel.Full,
  },
];

export type FinishReviewDialogProps = {
  currentAutonomyLevel?: ObservationsV1IssueAutonomyLevel | null;
  feedbackCount: number;
  hasReviewNotes: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (
    autonomyLevel: ObservationsV1IssueAutonomyLevel,
  ) => Promise<boolean>;
};

export const FinishReviewDialog = ({
  currentAutonomyLevel,
  feedbackCount,
  hasReviewNotes,
  isSubmitting,
  onClose,
  onSubmit,
}: FinishReviewDialogProps) => {
  const [decision, setDecision] = useState<ReviewDecision>(
    hasReviewNotes ? 'request_changes' : 'approve_build',
  );
  const selectedOption = REVIEW_DECISION_OPTIONS.find(
    option => option.value === decision,
  );

  return (
    <DialogComposite.Root open width="sm" onClose={onClose}>
      <DialogComposite.Content isLoading={isSubmitting}>
        <DialogComposite.Header>
          <DialogComposite.Title>Finish review</DialogComposite.Title>
          <DialogComposite.Subtitle>
            {feedbackCount}{' '}
            {feedbackCount === 1
              ? 'pending feedback item'
              : 'pending feedback items'}
          </DialogComposite.Subtitle>
        </DialogComposite.Header>
        <DialogComposite.Body>
          <div className={styles.content}>
            <Text as="p" variant="bodyMedium">
              Choose what Confidence should do with this plan.
            </Text>
            <div
              role="radiogroup"
              aria-label="Review decision"
              className={styles.options}
            >
              {REVIEW_DECISION_OPTIONS.map(option => {
                const requestChangesWithoutNotes =
                  option.value === 'request_changes' && !hasReviewNotes;
                const optionDescription = requestChangesWithoutNotes
                  ? 'Add feedback or a general instruction before requesting changes.'
                  : option.description;
                const autonomyLevelLabel = getAutonomyLevelLabel(
                  option.autonomyLevel,
                );
                const autonomyDescription =
                  currentAutonomyLevel === option.autonomyLevel
                    ? `Autonomy remains “${autonomyLevelLabel}”.`
                    : `Changes autonomy to “${autonomyLevelLabel}”.`;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={decision === option.value}
                    disabled={requestChangesWithoutNotes || isSubmitting}
                    className={styles.option}
                    data-selected={decision === option.value}
                    onClick={() => setDecision(option.value)}
                   data-track-source-anchor="0de4260337;finish-review-dialog-button">
                    <Text as="span" variant="bodyMediumBold">
                      {option.label}
                    </Text>
                    <Text
                      as="span"
                      variant="bodySmall"
                      semanticColor="textSubdued"
                    >
                      {optionDescription} {autonomyDescription}
                    </Text>
                  </button>
                );
              })}
            </div>
          </div>
        </DialogComposite.Body>
        <DialogComposite.Actions>
          <DialogComposite.Actions.Cancel />
          <DialogComposite.Actions.Primary
            disabled={!selectedOption}
            onClick={() => {
              if (!selectedOption) {
                return;
              }

              void onSubmit(selectedOption.autonomyLevel);
            }}
           data-track-source-anchor="d65844cb89;finish-review-dialog-primary">
            {selectedOption?.label ?? 'Finish review'}
          </DialogComposite.Actions.Primary>
        </DialogComposite.Actions>
      </DialogComposite.Content>
    </DialogComposite.Root>
  );
};
